import { Component, computed, inject, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { faArrowLeft, faArrowRight, faClose, faUpRightAndDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons';

import saveAs from 'file-saver';
import { map, shareReplay, Observable, tap } from 'rxjs';

import { NotificationLevel, NotificationsService } from '@eiq/notifications';

import { FileType, FileTypeTexts } from '@empathy/global/data';

import { MediaView } from '../../models/media-view.model';
import { Media } from '../../models/media.model';
import { MediaService } from '../../services/media.service';

@Component({
  selector: 'eiq-media-dialog',
  templateUrl: 'media-dialog.component.html',
  styleUrls: ['./media-dialog.component.scss']
})
export class MediaDialogComponent implements OnInit {
  currentPage$ = signal<number>(0);
  expandedMedia$ = computed(() => {
    if (this.expandedMediaIndex$() !== null) {
      return this.mappedMedia[this.expandedMediaIndex$()];
    }
    return null;
  });
  expandedMediaIndex$ = signal<number | null>(null);
  isDownloadingAll$ = signal(false);
  isDownloadingSelected$ = signal(false);
  mediaMapped$ = signal<MediaView[][]>([]);
  selectedMedia$ = signal<string[]>([]);

  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faClose = faClose;
  faUpRightAndDownLeftFromCenter = faUpRightAndDownLeftFromCenter;
  fileType = FileType;
  fileTypeTexts = FileTypeTexts;

  private dialogRef = inject(MatDialogRef<MediaDialogComponent>);
  private mediaService = inject(MediaService);
  private not = inject(NotificationsService);

  private mappedMedia: MediaView[] = [];

  constructor(@Inject(MAT_DIALOG_DATA) public data: { qId: string; media: Media[] }) {}

  ngOnInit(): void {
    this.dialogRef.addPanelClass('empathy-crosstab-media-details__dialog');

    this.mappedMedia = this.data.media.map((media) => ({
      url$: this.mediaService.getMediaUrl(media.mediaId).pipe(
        map((mediaResult) => mediaResult.url),
        shareReplay()
      ),
      id: media.mediaId,
      type: media.type as FileType,
      name: media.name
    }));

    const groupedMedia: MediaView[][] = [];
    const chunkSize = 9;

    for (let i = 0; i < this.mappedMedia.length; i += chunkSize) {
      groupedMedia.push(this.mappedMedia.slice(i, i + chunkSize));
    }

    this.mediaMapped$.set(groupedMedia);
  }

  onExpandMedia(indexOnPage: number): void {
    this.expandedMediaIndex$.set(this.currentPage$() + 1 * indexOnPage);
  }

  onPreviousPage(): void {
    this.currentPage$.update((page) => page - 1);
  }

  onNextPage(): void {
    this.currentPage$.update((page) => page + 1);
  }

  onSelect(mediaId: string): void {
    this.selectedMedia$.update((selectedMedia) => {
      if (selectedMedia.includes(mediaId)) {
        return selectedMedia.filter((selected) => selected !== mediaId);
      }
      return [...selectedMedia, mediaId];
    });
  }

  onDownloadAll(): void {
    this.isDownloadingAll$.set(true);
    this.download(this.data.media.map((media) => media.mediaId)).subscribe(() => this.isDownloadingAll$.set(false));
  }

  onDownloadSelected(): void {
    this.isDownloadingSelected$.set(true);
    this.download(this.selectedMedia$()).subscribe(() => this.isDownloadingSelected$.set(false));
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private download(mediaIds: string[]): Observable<boolean> {
    return this.mediaService.downloadMediaZip(mediaIds, this.data.qId).pipe(
      tap((zip) => {
        if (zip === null) {
          this.errorNotification();
        } else {
          saveAs(zip, `${this.data.qId}.zip`);
        }
      }),
      map((zip) => !!zip)
    );
  }

  private errorNotification(): void {
    this.not.open({ level: NotificationLevel.Error, title: 'Error', message: 'Error downloading' });
  }
}
